// src/pages/connect/FastBill.js
import React, { useState, useEffect } from "react";
import { Card, Button, Alert, Modal, Table, Spinner } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faPlug } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const FastBill = () => {
  const [connected, setConnected] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // ====== CHECK STATUS ON LOAD ======
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get("/api/integrations/status/fastbill");
        setConnected(res.data.connected);
      } catch (err) {
        console.error("Error checking FastBill status", err);
      }
    };
    checkStatus();
  }, []);

  // ====== CONNECT ======
  const handleConnect = async () => {
    try {
      const res = await axios.get("/api/integrations/fastbill/connect");
      // If OAuth flow → redirect to URL
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        setConnected(true);
      }
    } catch (err) {
      console.error("Error connecting FastBill", err);
    }
  };

  // ====== DISCONNECT ======
  const handleDisconnect = async () => {
    try {
      await axios.delete("/api/integrations/disconnect/fastbill");
      setConnected(false);
      setInvoices([]);
    } catch (err) {
      console.error("Error disconnecting FastBill", err);
    }
  };

  // ====== VIEW INVOICES ======
  const handleViewInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/integrations/fastbill/invoices");
      setInvoices(res.data.invoices || []);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching invoices", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <h3 className="mb-4">
        <FontAwesomeIcon icon={faPlug} className="me-2" />
        Connect FastBill
      </h3>

      <Card className="shadow-sm">
        <Card.Body>
          <FontAwesomeIcon icon={faFileAlt} size="2x" className="mb-3 text-info" />
          <Card.Title>FastBill</Card.Title>
          <Card.Text>
            Connect your FastBill account to automate invoice imports and financial sync.
          </Card.Text>

          {connected ? (
            <>
              <Alert variant="success" className="mt-3">
                ✅ Connected successfully!
              </Alert>
              <div className="d-flex gap-2 mt-3">
                <Button variant="danger" onClick={handleDisconnect}>
                  Disconnect
                </Button>
                <Button variant="primary" onClick={handleViewInvoices} disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : "View Invoices"}
                </Button>
              </div>
            </>
          ) : (
            <Button variant="info" className="mt-2" onClick={handleConnect}>
              Connect to FastBill
            </Button>
          )}
        </Card.Body>
      </Card>

      {/* ====== Invoices Modal ====== */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>FastBill Invoices</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {invoices.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr key={idx}>
                    <td>{inv.invoiceNumber || "-"}</td>
                    <td>{inv.customerName || "-"}</td>
                    <td>{inv.amount || "-"}</td>
                    <td>{inv.dueDate || "-"}</td>
                    <td>{inv.status || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No overdue invoices found ✅</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FastBill;
